/* eslint-disable max-lines */
import moment from 'moment'

import { SequenceTaskTemplate, SequenceTemplate, Task, TaskInput, TaskStatus, TaskTemplate } from '@types'

import { getStringFromDate } from '@utils/date'

import { DateRange, StartRowKey, ZoneIdKey } from '../types/task'

const getDateRangeForTask = (task: Task): DateRange => {
  const dateRange: DateRange = {
    startAt: moment(task.startAt).startOf('day'),
    endAt: moment(task.startAt)
      .add(Math.ceil(task.duration) - 1, 'days')
      .endOf('day'),
  }
  return dateRange
}

export const getDateRangeForGroupOfTasks = (tasks: Task[]): DateRange | undefined => {
  if (tasks.length == 0) {
    return undefined
  }

  const sortedByStart = [...tasks].sort((a, b) => (moment(a.startAt).isBefore(moment(b.startAt)) ? -1 : 1))
  const sortedByEnd = [...tasks].sort((a, b) =>
    moment(a.startAt).add(a.duration).isAfter(moment(b.startAt).add(b.duration)) ? -1 : 1,
  )

  const dateRange: DateRange = {
    startAt: moment(sortedByStart[0].startAt).startOf('day'),
    endAt: moment(sortedByEnd[0].startAt).add(sortedByEnd[0].duration, 'days').endOf('day'),
  }

  return dateRange
}

export const checkIfDateRangeOverlap = (range1: DateRange, range2: DateRange): boolean => {
  return (
    (range2.startAt.isSameOrAfter(range1.startAt) && range2.startAt.isSameOrBefore(range1.endAt)) ||
    (range2.endAt.isSameOrAfter(range1.startAt) && range2.endAt.isSameOrBefore(range1.endAt)) ||
    (range2.startAt.isSameOrBefore(range1.startAt) && range2.endAt.isSameOrAfter(range1.endAt))
  )
}

export const findFirstTaskWhereDateRangeOverlap = (task: Task, tasks: Task[]): Task | undefined => {
  const defaultDateRange = getDateRangeForTask(task)

  const taskOverlap = tasks.find((t) => {
    const dateRange = getDateRangeForTask(t)
    const value = checkIfDateRangeOverlap(defaultDateRange, dateRange)
    return value
  })

  return taskOverlap
}

export const getNextAvailableStartRowForSingleTask = (params: {
  task: Task
  tasks: Task[]
  startRowKey: StartRowKey
  zoneIdKey: ZoneIdKey
}): number => {
  const { task, tasks, startRowKey, zoneIdKey } = params

  const filteredTasks = tasks.filter((t) => t.id != task.id && t[zoneIdKey] == task[zoneIdKey])

  let formattedTask: Task | undefined
  let startRow = task[startRowKey]

  while (formattedTask == undefined) {
    const filteredTaskForStartRow = filteredTasks.filter((t) => t[startRowKey] == startRow)
    const taskOverlap = findFirstTaskWhereDateRangeOverlap(
      { ...task, [startRowKey]: startRow },
      filteredTaskForStartRow,
    )

    if (!taskOverlap) {
      formattedTask = { ...task, [startRowKey]: startRow }
    } else {
      startRow++
    }
  }

  return startRow
}

/**
 *  This function checks if there is any overlap between the task and the tasks in the same row and same zone
 *  If there is no overlap, it returns the startRow of the task
 *  Otherwise, it returns the startRow of the next task
 */
export const getNextAvailableStartRowForGroupOfTasks = (params: {
  task: Task
  tasks: Task[]
  startRowKey: StartRowKey
  zoneIdKey: ZoneIdKey
}): number => {
  const { task, tasks, startRowKey, zoneIdKey } = params

  const startRow = task[startRowKey]

  const filteredTasks = tasks.filter(
    (t) => t.id != task.id && t[zoneIdKey] == task[zoneIdKey] && t[startRowKey] == startRow,
  )

  if (filteredTasks.length == 0) return startRow

  const dateRangeTask = getDateRangeForTask(task)

  const hasOverlap = filteredTasks.some((task) => {
    const taskDateRange = getDateRangeForTask(task)
    return checkIfDateRangeOverlap(dateRangeTask, taskDateRange)
  })

  return hasOverlap ? startRow + 1 : startRow
}

/**
 *  This function checks if the added task is the first task in the same row and same zone
 *  If it is, it returns the startRow of the added task
 *  Otherwise, it returns the startRow of the next task
 */
export const getTasksWithCorrectStartRow = (params: {
  addedTasks: Task[]
  tasks: Task[]
  startRowKey: StartRowKey
  zoneIdKey: ZoneIdKey
}): Task[] => {
  const { addedTasks, tasks, startRowKey, zoneIdKey } = params

  if (addedTasks.length == 1) {
    const task = addedTasks[0]
    const startRow = getNextAvailableStartRowForSingleTask({ task, tasks, startRowKey, zoneIdKey })
    const mappedTasks = [{ ...task, [startRowKey]: startRow }]
    return mappedTasks
  }

  let correctedStartRowPadding: number | undefined
  let startRowPadding = 0

  while (correctedStartRowPadding == undefined) {
    const isSameStartRowPadding = addedTasks.every((task) => {
      const updatedTask = { ...task, [startRowKey]: task[startRowKey] + startRowPadding }
      const startRow = getNextAvailableStartRowForGroupOfTasks({ task: updatedTask, tasks, startRowKey, zoneIdKey })
      const padding = startRow - task[startRowKey]
      return padding == startRowPadding
    })

    if (isSameStartRowPadding) {
      correctedStartRowPadding = startRowPadding
    } else {
      startRowPadding++
    }
  }

  const mappedTasks = addedTasks.map((t) => ({ ...t, [startRowKey]: t[startRowKey] + startRowPadding }))
  return mappedTasks
}

// Handle Weekend

const getCountOfWeekEndDay = (date: Date, duration: number) => {
  let countOfWeekDay = 0

  for (let index = 1; index < duration + 1; index++) {
    const currentDay = moment(date).add(index, 'day')
    const weekDay = currentDay.weekday()
    if (weekDay == 5 || weekDay == 6) {
      countOfWeekDay += 1
    }
  }

  return countOfWeekDay
}

const getPaddingInDaysBetweenDate = (prevDate: Date, nextDate: Date, escapeWeekEnd?: boolean): number => {
  const startDate = moment(prevDate)

  const endDate = moment(nextDate)

  const countOfDays = endDate.diff(startDate, 'days')

  let padding = countOfDays

  if (escapeWeekEnd) {
    const countOfWeekEndDay = getCountOfWeekEndDay(prevDate, countOfDays)
    padding -= countOfWeekEndDay
  }

  return padding
}

const getPaddingInDaysBetweenTask = (prevTask: Task, nextTask: Task): number => {
  const startDate = moment(prevTask.startAt)
    .add(prevTask.duration - 1, 'days')
    .endOf('day')
    .toDate()

  const endDate = moment(nextTask.startAt).toDate()

  const padding = getPaddingInDaysBetweenDate(startDate, endDate, true)

  return padding
}

const isEven = (value: number): boolean => {
  return value % 2 == 0
}

export const getVirtualDuration = (date: Date, duration: number): number => {
  let countOfWeekEndDay = getCountOfWeekEndDay(date, duration - 1)
  if (!isEven(countOfWeekEndDay)) {
    countOfWeekEndDay += 1
  }

  let finalizedCountOfWeekEndDay = 0
  while (countOfWeekEndDay != finalizedCountOfWeekEndDay) {
    finalizedCountOfWeekEndDay = countOfWeekEndDay

    const newDuration = duration + finalizedCountOfWeekEndDay - 1

    const nextCountOfWeekEndDay = getCountOfWeekEndDay(date, newDuration)
    if (!isEven(countOfWeekEndDay)) {
      countOfWeekEndDay += 1
    }

    countOfWeekEndDay = nextCountOfWeekEndDay
  }

  const virtualDuration = duration + finalizedCountOfWeekEndDay

  return virtualDuration
}

export const getNextMonday = (date: Date): Date => {
  let nextStartDate = moment(date)

  const weekDay = nextStartDate.weekday()
  if (weekDay == 5) {
    nextStartDate = moment(nextStartDate).add(2, 'days')
  } else if (weekDay == 6) {
    nextStartDate = moment(nextStartDate).add(1, 'days')
  }

  return nextStartDate.startOf('day').toDate()
}

export const adjustDateToWeekday = (date: moment.Moment, direction: 'start' | 'end'): moment.Moment => {
  const day = date.day()
  if (day === 0) {
    return direction === 'start' ? date.subtract(2, 'days') : date.add(1, 'day')
  }
  if (day === 6) {
    return direction === 'start' ? date.subtract(1, 'day') : date.add(2, 'days')
  }
  return date
}

export const calculateWeekdaysByDateAndDuration = (date: moment.Moment, duration: number): number => {
  let days = 0

  for (let i = 0; i < duration; i++) {
    const current = date.clone().add(i, 'days')
    if (current.day() !== 0 && current.day() !== 6) {
      days++
    }
  }

  return days
}

export const calculateWeekdaysBetween = (start: moment.Moment, end: moment.Moment): number => {
  let days = 0
  const totalDays = end.diff(start, 'days') + 1

  for (let i = 0; i < totalDays; i++) {
    const current = start.clone().add(i, 'days')
    if (current.day() !== 0 && current.day() !== 6) {
      days++
    }
  }

  return days
}

const getNextCorrectStartDate = (date: Date, duration: number): Date => {
  let nextStartDate = moment(date)

  for (let index = 1; index < duration + 1; index++) {
    nextStartDate = moment(nextStartDate).add(1, 'days')
    nextStartDate = moment(getNextMonday(nextStartDate.toDate()))
  }

  return nextStartDate.toDate()
}

export const getTasksByStartRow = (params: { tasks: Task[]; startRowKey: StartRowKey }): { [key: string]: Task[] } => {
  const { tasks, startRowKey } = params

  const tasksByStartRow = tasks.reduce((acc, task) => {
    const startRow = `${task[startRowKey]}`
    const tasks = acc[startRow] || []
    tasks.push(task)
    return { ...acc, [startRow]: tasks }
  }, {} as { [key: string]: Task[] })

  return tasksByStartRow
}

// This function does not care about zoneId
export const getTasksSortedByStartRow = (params: { tasks: Task[]; startRowKey: StartRowKey }) => {
  const { tasks, startRowKey } = params

  const copy = [...tasks]
  const sortedTasks = copy.sort((a, b) =>
    a[startRowKey] < b[startRowKey] ? -1 : a[startRowKey] > b[startRowKey] ? 1 : 0,
  )
  return sortedTasks
}

// This function does not care about zoneId
export const getTasksSortedByStartDate = (tasks: Task[]) => {
  const copy = [...tasks]
  const sortedTasks = copy.sort((a, b) =>
    moment(a.startAt).startOf('day').isBefore(moment(b.startAt).startOf('day')) ? -1 : 1,
  )
  return sortedTasks
}

/**
 *  This function does not care about zoneId
 *  It sorts the tasks by startRow and returns the sorted tasks
 *  If the array of tasks is empty, it returns an empty array
 */
export const getTasksWithUpdatedStartRow = (params: { tasks: Task[]; startRow: number; startRowKey: StartRowKey }) => {
  const { tasks, startRow, startRowKey } = params

  if (tasks.length == 0) {
    return []
  }

  const sortedTasks = getTasksSortedByStartRow({ tasks, startRowKey })

  const defaultStartRow = sortedTasks[0][startRowKey]
  const paddingStartRow = startRow - defaultStartRow

  const mappedTasks = sortedTasks.map((task) => ({ ...task, [startRowKey]: task[startRowKey] + paddingStartRow }))
  return mappedTasks
}

export const getTasksWithCorrectPadding = (params: {
  tasks: Task[]
  startDate: Date
  startRowKey: StartRowKey
}): Task[] => {
  const { tasks, startDate, startRowKey } = params

  if (tasks.length == 0) {
    return []
  }

  const sortedTasksByStartDate = tasks.sort((a, b) =>
    moment(a.startAt).startOf('day').isBefore(moment(b.startAt).startOf('day')) ? -1 : 1,
  )

  // Group tasks by startRow
  const tasksByStartRow = getTasksByStartRow({ tasks: sortedTasksByStartDate, startRowKey })

  // Set start task
  const startTask = sortedTasksByStartDate[0]

  // If date is weekend move to next monday
  const defaultStartDate = moment(getNextMonday(startDate))

  const tasksWithCorrectPadding: Task[] = Object.keys(tasksByStartRow).reduce((acc, startRow) => {
    const tasks = tasksByStartRow[startRow]

    // Get padding between start task and first task of grouped tasks by startRow
    const padding = getPaddingInDaysBetweenDate(
      moment(startTask.startAt).toDate(),
      moment(tasks[0].startAt).toDate(),
      true,
    )

    // Get the next start date based on padding and escape week ends
    let nextStartDate = moment(getNextCorrectStartDate(defaultStartDate.toDate(), padding))

    const paddingBetweenTasks: { [key: string]: number } = { [tasks[0].id]: 0 }

    for (let index = 1; index < tasks.length; index++) {
      const task = tasks[index]
      const prevTask = tasks[index - 1]

      const padding = getPaddingInDaysBetweenTask(prevTask, task)

      paddingBetweenTasks[task.id] = padding
    }

    const mappedTasks: Task[] = []

    for (const task of tasks) {
      const paddingBetween = paddingBetweenTasks[task.id]

      nextStartDate = moment(getNextCorrectStartDate(nextStartDate.toDate(), paddingBetween))

      const virtualDuration = getVirtualDuration(nextStartDate.toDate(), task.requiredDuration)

      const mappedTask: Task = {
        ...task,
        startAt: nextStartDate.startOf('day').toDate(),
        // startAtKey: moment(nextStartDate.startOf('day')).format('YYYY-MM-DD'),
        duration: virtualDuration,
      }

      mappedTasks.push(mappedTask)

      nextStartDate = moment(nextStartDate).add(virtualDuration, 'days')
      // Escape weekEnd days
      nextStartDate = moment(getNextMonday(nextStartDate.toDate()))
    }

    return [...acc, ...mappedTasks]
  }, [] as Task[])

  return tasksWithCorrectPadding
}

/**
 *  This function takes a startDate, startRow, zoneId and sequence template and returns an array of tasks
 *  It sorts the tasks by startDay and returns the sorted tasks
 *  If the sequence is empty, it returns an empty array
 */
export const getTasksFromSequenceTemplate = (params: {
  startDate: Date
  startRow: number
  startRowKey: StartRowKey
  zoneId?: string
  zoneIdKey: ZoneIdKey
  sequence: SequenceTemplate
}): Task[] => {
  const { startDate, startRow, startRowKey, zoneId, zoneIdKey, sequence } = params

  if (sequence.tasks.length == 0) {
    return []
  }

  // Tasks with correct startRow
  const tasks = sequence.tasks.map((task) => ({ ...task, startRow: task.startRow + startRow }))

  const sortedTasksByStartDay = tasks.sort((a, b) => (a.startDay < b.startDay ? -1 : 1))

  // Group tasks by startRow
  const tasksByStartRow = sortedTasksByStartDay.reduce((acc, task) => {
    const startRow = `${task.startRow}`
    const tasks = acc[startRow] || []
    tasks.push(task)
    return { ...acc, [startRow]: tasks }
  }, {} as { [key: string]: SequenceTaskTemplate[] })

  // Set start task
  const startTask = sortedTasksByStartDay[0]

  // If date is weekend move to next monday
  const defaultStartDate = moment(getNextMonday(startDate))

  const tasksWithCorrectPadding: Task[] = Object.keys(tasksByStartRow).reduce((acc, startRow) => {
    const tasks = tasksByStartRow[startRow]

    // Get padding between start task and first task of grouped tasks by startRow
    const padding = tasks[0].startDay - startTask.startDay

    // Get the next start date based on padding and escape week ends
    let nextStartDate = moment(getNextCorrectStartDate(defaultStartDate.toDate(), padding))

    const paddingBetweenTasks: { [key: string]: number } = { [tasks[0].id]: 0 }

    for (let index = 1; index < tasks.length; index++) {
      const task = tasks[index]
      const prevTask = tasks[index - 1]

      const padding = task.startDay - (prevTask.startDay + prevTask.duration)

      paddingBetweenTasks[task.id] = padding
    }

    const mappedTasks: Task[] = []

    for (const task of tasks) {
      const paddingBetween = paddingBetweenTasks[task.id]

      nextStartDate = moment(getNextCorrectStartDate(nextStartDate.toDate(), paddingBetween))

      const virtualDuration = getVirtualDuration(nextStartDate.toDate(), task.duration)

      const mappedTask: Task = {
        ...task,
        __typename: 'Task',
        idNumber: 0,
        progress: 0,
        status: TaskStatus.StatusToDo,

        startAt: nextStartDate.startOf('day').toDate(),
        endAt: moment(nextStartDate).add(virtualDuration, 'days').endOf('day').toDate(),
        requiredDuration: task.duration,
        duration: virtualDuration,

        startRowByZone: task.startRow,
        startRowByCompany: task.startRow,
        startRowByTrade: task.startRow,
        [startRowKey]: task.startRow,

        companyId: task.company?.id,
        company: task.company,
        tradeKey: task.trade.key,
        trade: task.trade,
        [zoneIdKey]: zoneId,

        activities: [],
        comments: [],
      }

      mappedTasks.push(mappedTask)

      nextStartDate = moment(nextStartDate).add(virtualDuration, 'days')
      // Escape weekEnd days
      nextStartDate = moment(getNextMonday(nextStartDate.toDate()))
    }

    return [...acc, ...mappedTasks]
  }, [] as Task[])

  return tasksWithCorrectPadding
}

export const getTasksFromTaskTemplate = (params: {
  startDate: Date
  startRow: number
  startRowKey: StartRowKey
  zoneId?: string
  zoneIdKey: ZoneIdKey
  taskTemplate: TaskTemplate
}): Task => {
  const { startDate, startRow, startRowKey, zoneId, zoneIdKey, taskTemplate } = params

  // If date is weekend move to next monday
  const defaultStartDate = moment(getNextMonday(startDate))

  const virtualDuration = getVirtualDuration(defaultStartDate.toDate(), taskTemplate.duration)

  const task: Task = {
    ...taskTemplate,
    __typename: 'Task',
    idNumber: 0,
    progress: 0,
    status: TaskStatus.StatusToDo,

    startAt: defaultStartDate.toDate(),
    endAt: moment(defaultStartDate).add(virtualDuration, 'days').endOf('day').toDate(),
    requiredDuration: taskTemplate.duration,
    duration: virtualDuration,

    startRowByZone: startRow,
    startRowByCompany: startRow,
    startRowByTrade: startRow,
    [startRowKey]: startRow,

    tradeKey: taskTemplate.trade.key,
    trade: taskTemplate.trade,
    companyId: taskTemplate.company?.id,
    company: taskTemplate.company,
    [zoneIdKey]: zoneId,

    activities: [],
    comments: [],
  }

  return task
}

export const updateTaskByStartDateAndRequiredDuration = (
  task: Task,
  startDate: Date,
  requiredDuration: number,
): Task => {
  // If date is weekend move to next monday
  const defaultStartDate = moment(getNextMonday(startDate))

  const virtualDuration = getVirtualDuration(defaultStartDate.toDate(), requiredDuration)

  return {
    ...task,
    startAt: defaultStartDate.toDate(),
    requiredDuration,
    duration: virtualDuration,
  }
}

export const getTaskInputFromTask = (task: Task): TaskInput => {
  const taskInput: TaskInput = {
    name: task.name,
    description: task.description,

    startAt: getStringFromDate(task.startAt),
    duration: Math.ceil(task.duration),
    requiredDuration: task.requiredDuration,
    workforce: task.workforce,

    startRowByZone: task.startRowByZone,
    startRowByCompany: task.startRowByCompany,
    startRowByTrade: task.startRowByTrade,

    zoneId: task.zoneId,
    tradeKey: task.tradeKey,
    companyId: task.companyId,

    projectFileId: task.projectFilePositions ? task.projectFileId : undefined,
    projectFilePositions: task.projectFilePositions
      ? [
          {
            x: task.projectFilePositions[0].x,
            y: task.projectFilePositions[0].y,
          },
        ]
      : undefined,
  }

  return taskInput
}

export const getTaskInputFromTaskWithoutStartRows = (task: Task): TaskInput => {
  const taskInput: TaskInput = {
    name: task.name,
    description: task.description,

    startAt: getStringFromDate(task.startAt),
    duration: Math.ceil(task.duration),
    requiredDuration: task.requiredDuration,
    workforce: task.workforce,

    tradeKey: task.tradeKey,
    companyId: task.companyId,
    zoneId: task.zoneId,

    projectFileId: task.projectFilePositions ? task.projectFileId : undefined,
    projectFilePositions: task.projectFilePositions
      ? [
          {
            x: task.projectFilePositions[0].x,
            y: task.projectFilePositions[0].y,
          },
        ]
      : undefined,
  }

  return taskInput
}

export const getTaskInputFromTaskByStartRowKey = (task: Task, startRowKey: StartRowKey): TaskInput => {
  const taskInput: TaskInput = {
    name: task.name,
    description: task.description,

    startAt: getStringFromDate(task.startAt),
    duration: Math.ceil(task.duration),
    requiredDuration: task.requiredDuration,
    workforce: task.workforce,
    color: task.color,

    [startRowKey]: task[startRowKey],

    tradeKey: task.tradeKey,
    companyId: task.companyId,
    zoneId: task.zoneId,

    projectFileId: task.projectFilePositions ? task.projectFileId : undefined,
    projectFilePositions: task.projectFilePositions
      ? [
          {
            x: task.projectFilePositions[0].x,
            y: task.projectFilePositions[0].y,
          },
        ]
      : undefined,
  }

  return taskInput
}
